import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@pazportal/ui';
import { useEstimate } from '../hooks/useEstimate';
import {
  createDocumentDownloadUrl,
  useProposalDocuments,
  withOwnDocumentFlag,
  type ProposalDocument,
} from '../hooks/useEstimateDocuments';
import { useEstimatorSettings } from '../hooks/useEstimatorSettings';
import { useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { db } from '../lib/db';
import { describeTaskError, invokeTriggerTask } from '../lib/triggerTask';
import { buildProposalHtml, buildProposalSubject, type ProposalEmailInput } from '../lib/proposalEmail';
import { formatCurrency, formatSolutionType, formatDate } from '../lib/formatters';
import { adminCustomerUrl } from '../lib/adminUrl';
import type { EstimateDetailRow, EstimateLineItemRow } from '../lib/estimateTypes';

const JOB_TIMEOUT_MS = 120_000;
const POLL_INTERVAL_MS = 3_000;

interface PdfJob {
  runId: string | null;
  queuedAt: number;
  /** updated_at (ms) of this estimate's document before the job was queued. */
  previousUpdatedAt: number | null;
}

interface SendJob {
  runId: string | null;
  queuedAt: number;
  previousSentAt: string | null;
}

type JobStatus =
  | { kind: 'idle' }
  | { kind: 'queuing' }
  | { kind: 'queued'; runId: string | null }
  | { kind: 'done'; runId: string | null }
  | { kind: 'timeout'; runId: string | null }
  | { kind: 'error'; message: string };

function minuteKey(prefix: string, id: string): string {
  return `${prefix}-${id}-${Math.floor(Date.now() / 60_000)}`;
}

function emailInputFromEstimate(est: EstimateDetailRow, footer: string | null): ProposalEmailInput {
  return {
    estimateNumber: est.estimate_number,
    contactName: est.contact_name,
    customerName: est.customer?.name ?? null,
    locationName: est.location?.name ?? null,
    solutionType: est.solution_type,
    hardwareCents: est.hardware_subtotal_cents ?? 0,
    laborCents: est.labor_subtotal_cents ?? 0,
    contingencyCents: est.contingency_cents ?? 0,
    contingencyPct: est.contingency_pct ?? 0,
    totalCents: est.total_cents ?? 0,
    validUntil: est.valid_until,
    footer,
  };
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * React Router keeps the same element mounted when only `:id` changes, so
 * the page body is keyed by the estimate id: job status, accept results and
 * download errors never carry over from one estimate to the next.
 */
export function EstimateDetailPage() {
  const { id } = useParams<{ id: string }>();
  return <EstimateDetail key={id ?? 'none'} id={id} />;
}

function EstimateDetail({ id }: { id: string | undefined }) {
  const routerLocation = useLocation();
  const qc = useQueryClient();

  const [pdfJob, setPdfJob] = useState<PdfJob | null>(null);
  const [pdfStatus, setPdfStatus] = useState<JobStatus>({ kind: 'idle' });
  const [sendJob, setSendJob] = useState<SendJob | null>(null);
  const [sendStatus, setSendStatus] = useState<JobStatus>({ kind: 'idle' });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptedDeploymentId, setAcceptedDeploymentId] = useState<string | null>(null);

  const { data, isLoading, error } = useEstimate(id, {
    refetchInterval: sendJob ? POLL_INTERVAL_MS : false,
  });
  const est = data?.estimate;
  const { data: docRows, error: docsError } = useProposalDocuments(est?.customer_id, {
    refetchInterval: pdfJob ? POLL_INTERVAL_MS : false,
  });
  const docs = useMemo(
    () => withOwnDocumentFlag(docRows, est?.customer_id, est?.estimate_number),
    [docRows, est?.customer_id, est?.estimate_number],
  );
  const { data: snapshot, isLoading: snapshotLoading, error: snapshotError } = useIntegrationStatus();
  // The proposal footer is part of the email body; the wizard preview shows
  // the same value, so what the rep saw is what goes out.
  const { data: defaults } = useEstimatorSettings();

  const saveNotice = (routerLocation.state as { notice?: string } | null)?.notice ?? null;

  // ── PDF job: resolve when this estimate's document appears or is refreshed ──
  useEffect(() => {
    if (!pdfJob || !docs) return;
    const own = docs.find((d) => d.forThisEstimate);
    if (!own) return;
    const updatedAt = new Date(own.updated_at).getTime();
    if (pdfJob.previousUpdatedAt === null || updatedAt > pdfJob.previousUpdatedAt) {
      setPdfJob(null);
      setPdfStatus({ kind: 'done', runId: pdfJob.runId });
    }
  }, [docs, pdfJob]);

  useEffect(() => {
    if (!pdfJob) return;
    const timer = window.setTimeout(() => {
      setPdfJob(null);
      setPdfStatus({ kind: 'timeout', runId: pdfJob.runId });
    }, JOB_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [pdfJob]);

  // ── Send job: resolve when proposal_sent_at moves ──
  useEffect(() => {
    if (!sendJob || !est) return;
    if (est.proposal_sent_at && est.proposal_sent_at !== sendJob.previousSentAt) {
      setSendJob(null);
      setSendStatus({ kind: 'done', runId: sendJob.runId });
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate-metrics'] });
      qc.invalidateQueries({ queryKey: ['integration-status'] });
    }
  }, [est, sendJob, qc]);

  useEffect(() => {
    if (!sendJob) return;
    const timer = window.setTimeout(() => {
      setSendJob(null);
      setSendStatus({ kind: 'timeout', runId: sendJob.runId });
    }, JOB_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [sendJob]);

  async function handleGeneratePdf() {
    if (!est) return;
    const own = docs?.find((d) => d.forThisEstimate) ?? null;
    setPdfStatus({ kind: 'queuing' });
    try {
      const { runId } = await invokeTriggerTask(
        'proposal-pdf-generate',
        { estimateId: est.id },
        minuteKey('pdf', est.id),
      );
      setPdfJob({
        runId,
        queuedAt: Date.now(),
        previousUpdatedAt: own ? new Date(own.updated_at).getTime() : null,
      });
      setPdfStatus({ kind: 'queued', runId });
    } catch (err) {
      setPdfStatus({ kind: 'error', message: describeTaskError(err, 'The PDF job could not be started.') });
    }
  }

  async function handleSend() {
    if (!est || !est.contact_email) return;
    const input = emailInputFromEstimate(est, defaults?.proposalFooter ?? null);
    setSendStatus({ kind: 'queuing' });
    try {
      const { runId } = await invokeTriggerTask(
        'm365-send-proposal',
        {
          estimateId: est.id,
          toEmail: est.contact_email,
          toName: est.contact_name ?? undefined,
          ccEmails: est.cc_emails ?? [],
          subject: buildProposalSubject(input),
          bodyHtml: buildProposalHtml(input),
          followupDays: est.followup_days ?? undefined,
        },
        minuteKey('m365-send', est.id),
      );
      setSendJob({ runId, queuedAt: Date.now(), previousSentAt: est.proposal_sent_at });
      setSendStatus({ kind: 'queued', runId });
    } catch (err) {
      setSendStatus({ kind: 'error', message: describeTaskError(err, 'The send could not be started.') });
    }
  }

  async function handleDownload(doc: ProposalDocument) {
    setDownloadError(null);
    setDownloadingId(doc.id);
    try {
      const url = await createDocumentDownloadUrl(doc.s3_key);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) window.location.assign(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Could not create a download link.');
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleAcceptEstimate() {
    if (!id) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      // accept_estimate creates the deployment, marks the estimate accepted,
      // and records a signed handoff event.
      const { data: deploymentId, error: rpcError } = await db.rpc('accept_estimate', { p_estimate_id: id });
      if (rpcError) throw new Error(rpcError.message);
      setAcceptedDeploymentId(typeof deploymentId === 'string' ? deploymentId : null);
      qc.invalidateQueries({ queryKey: ['estimate', id] });
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate-metrics'] });
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Could not accept this estimate.');
    } finally {
      setAccepting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading estimate</span>
      </div>
    );
  }

  if (error || !est) {
    return (
      <div className="p-6 text-center">
        <p className="text-error" role="alert">{error instanceof Error ? error.message : 'Estimate not found.'}</p>
        <Link to="/dashboard" className="inline-flex min-h-11 items-center mt-2 text-sm text-primary hover:underline">Back to dashboard</Link>
      </div>
    );
  }

  const items = data?.lineItems ?? [];
  const hwItems = items.filter((i) => i.type === 'hardware');
  const laborItems = items.filter((i) => i.type === 'labor');
  const otherItems = items.filter((i) => i.type !== 'hardware' && i.type !== 'labor');
  const customer = est.customer;
  const location = est.location;
  const canAccept = est.status === 'draft' || est.status === 'sent' || est.status === 'viewed';
  const canEdit = est.status === 'draft';
  const ownDoc = docs?.find((d) => d.forThisEstimate) ?? null;

  const m365State = snapshot?.m365?.state;
  const m365Live = m365State === 'live';
  const sendBlockedReason: string | null = snapshotLoading
    ? 'Checking Microsoft 365 status…'
    : snapshotError
      ? `Microsoft 365 status could not be checked, so sending is disabled: ${snapshotError instanceof Error ? snapshotError.message : 'request failed'}`
      : !m365Live
        ? 'Microsoft 365 is not connected: no proposal has been delivered through it from this system yet, so sending is disabled.'
        : !est.contact_email
          ? 'Add a contact email to this estimate before sending.'
          : est.status === 'accepted' || est.status === 'rejected' || est.status === 'expired'
            ? `This estimate is ${est.status}; sending is disabled.`
            : null;
  const sendBusy = sendStatus.kind === 'queuing' || sendStatus.kind === 'queued';

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      <Link to="/dashboard" className="inline-flex min-h-11 items-center text-sm text-primary hover:underline">&larr; Back to dashboard</Link>

      {saveNotice && (
        <p className="rounded-lg border border-warning bg-warning-soft px-3 py-2 text-sm text-text" role="alert">{saveNotice}</p>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text font-mono">{est.estimate_number}</h1>
          <p className="text-sm text-text-muted mt-0.5">
            {customer?.id ? (
              <a href={adminCustomerUrl(customer.id)} className="text-primary hover:underline">{customer.name}</a>
            ) : (
              est.contact_name ?? 'No customer'
            )}
            {' · '}
            {location?.name ?? 'No location'}
            {' · '}
            {est.solution_type ? formatSolutionType(est.solution_type) : 'No solution selected'}
            {' · '}
            {formatDate(est.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={est.status ?? 'draft'} />
          {canEdit && (
            <Link
              to={`/estimates/${est.id}/edit`}
              className="inline-flex min-h-11 items-center px-4 py-2 text-sm font-medium rounded-lg bg-primary text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Edit estimate
            </Link>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <InfoCard label="Hardware" value={formatCurrency(est.hardware_subtotal_cents ?? 0)} />
        <InfoCard label="Labor" value={formatCurrency(est.labor_subtotal_cents ?? 0)} hint={est.total_labor_hours ? `${est.total_labor_hours} h` : undefined} />
        <InfoCard label={`Contingency (${est.contingency_pct ?? 0}%)`} value={formatCurrency(est.contingency_cents ?? 0)} />
        <InfoCard label="Total" value={formatCurrency(est.total_cents ?? 0)} bold />
      </div>

      {/* Proposal details */}
      <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="details-heading">
        <h2 id="details-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Proposal details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Detail label="Contact" value={est.contact_name} />
          <Detail label="Email" value={est.contact_email} />
          <Detail label="Phone" value={est.contact_phone} />
          <Detail label="CC" value={est.cc_emails && est.cc_emails.length > 0 ? est.cc_emails.join(', ') : null} />
          <Detail label="Valid until" value={est.valid_until ? formatDate(est.valid_until) : null} emptyText="No validity window set" />
          <Detail label="Follow-up" value={est.followup_days ? `${est.followup_days} business days after sending` : null} emptyText="No reminder set" />
          <Detail label="Sent" value={est.proposal_sent_at ? formatDate(est.proposal_sent_at) : null} emptyText="Not sent" />
          <Detail label="Last reply" value={est.m365_last_reply_at ? formatDate(est.m365_last_reply_at) : null} emptyText="No reply recorded" />
        </dl>
        {est.scope_notes && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Assessment notes</h3>
            <p className="text-sm text-text whitespace-pre-wrap">{est.scope_notes}</p>
          </div>
        )}
      </section>

      {/* Hardware items */}
      <LineItemSection
        title="Hardware BOM"
        items={hwItems}
        emptyText="No hardware lines on this estimate."
        columns={[
          { label: 'Qty', render: (i) => String(i.quantity ?? 0) },
          { label: 'Unit cost', render: (i) => formatCurrency(i.unit_cost_cents ?? 0) },
          { label: 'Unit sell', render: (i) => formatCurrency(i.unit_sell_cents ?? 0) },
          { label: 'Line total', render: (i) => formatCurrency(Math.round((i.quantity ?? 0) * (i.unit_sell_cents ?? 0))), strong: true },
        ]}
      />

      {/* Labor items */}
      <LineItemSection
        title="Labor"
        items={laborItems}
        emptyText="No labor lines on this estimate."
        columns={[
          { label: 'Hours', render: (i) => String(i.quantity ?? 0) },
          { label: 'Rate', render: (i) => `${formatCurrency(i.unit_sell_cents ?? 0)}/hr` },
          { label: 'Line total', render: (i) => formatCurrency(Math.round((i.quantity ?? 0) * (i.unit_sell_cents ?? 0))), strong: true },
        ]}
      />

      {otherItems.length > 0 && (
        <LineItemSection
          title="Other lines"
          items={otherItems}
          emptyText=""
          columns={[
            { label: 'Type', render: (i) => i.type },
            { label: 'Qty', render: (i) => String(i.quantity ?? 0) },
            { label: 'Unit sell', render: (i) => formatCurrency(i.unit_sell_cents ?? 0) },
            { label: 'Line total', render: (i) => formatCurrency(Math.round((i.quantity ?? 0) * (i.unit_sell_cents ?? 0))), strong: true },
          ]}
        />
      )}

      {/* Documents */}
      <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="documents-heading">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 id="documents-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider">Documents</h2>
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={pdfStatus.kind === 'queuing' || pdfStatus.kind === 'queued' || !est.customer_id}
            className="inline-flex min-h-11 items-center px-4 py-2 text-sm font-medium rounded-lg border border-border bg-surface text-text hover:bg-surface-strong disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {pdfStatus.kind === 'queuing'
              ? 'Starting…'
              : pdfStatus.kind === 'queued'
                ? 'Rendering…'
                : ownDoc
                  ? 'Regenerate proposal PDF'
                  : 'Generate proposal PDF'}
          </button>
        </div>

        {!est.customer_id && (
          <p className="text-xs text-text-muted mb-2">This estimate has no customer, so a PDF cannot be filed under a customer's documents.</p>
        )}
        {pdfStatus.kind === 'queued' && (
          <p className="text-xs text-text-muted mb-2" role="status">
            Job queued{pdfStatus.runId ? ` (run ${pdfStatus.runId})` : ''}. Watching for the PDF to appear…
          </p>
        )}
        {pdfStatus.kind === 'done' && (
          <p className="text-xs text-success mb-2" role="status">PDF rendered and filed under the customer's documents.</p>
        )}
        {pdfStatus.kind === 'timeout' && (
          <p className="text-xs text-warning mb-2" role="alert">
            No PDF was recorded within 2 minutes{pdfStatus.runId ? ` (run ${pdfStatus.runId})` : ''}. The job may still be running or may have failed on the job runner; check the run in Trigger.dev and refresh this page.
          </p>
        )}
        {pdfStatus.kind === 'error' && (
          <p className="text-xs text-error mb-2" role="alert">{pdfStatus.message}</p>
        )}
        {docsError && (
          <p className="text-xs text-error mb-2" role="alert">{docsError instanceof Error ? docsError.message : 'Documents could not be loaded.'}</p>
        )}
        {downloadError && (
          <p className="text-xs text-error mb-2" role="alert">{downloadError}</p>
        )}

        {docs && docs.length === 0 && !docsError && (
          <p className="text-sm text-text-muted">No proposal documents on file for this customer.</p>
        )}
        {docs && docs.length > 0 && (
          <ul className="divide-y divide-divider" aria-label="Proposal documents">
            {docs.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-text truncate">
                    {doc.title}
                    {doc.forThisEstimate && (
                      <span className="ml-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary-soft text-primary">This estimate</span>
                    )}
                  </p>
                  <p className="text-xs text-text-faint">
                    {formatDate(doc.updated_at)} {'·'} {formatBytes(doc.file_size_bytes)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                  className="inline-flex min-h-11 items-center px-3 text-sm font-medium text-primary hover:underline disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                >
                  {downloadingId === doc.id ? 'Preparing…' : 'Download'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Send via M365 */}
      <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="send-heading">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 id="send-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider">Send proposal</h2>
            <p className="text-xs text-text-muted mt-1">
              Emails the proposal summary to {est.contact_email ?? 'the contact'} from the PazNetworks sales mailbox
              {est.cc_emails && est.cc_emails.length > 0 ? `, copying ${est.cc_emails.join(', ')}` : ''}.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSend}
            disabled={Boolean(sendBlockedReason) || sendBusy}
            className="inline-flex min-h-11 items-center px-4 py-2 text-sm font-medium rounded-lg bg-primary text-text-inverse hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {sendStatus.kind === 'queuing'
              ? 'Starting…'
              : sendStatus.kind === 'queued'
                ? 'Sending…'
                : est.proposal_sent_at
                  ? 'Resend via M365'
                  : 'Send via M365'}
          </button>
        </div>
        {sendBlockedReason && <p className="text-xs text-text-muted mt-2">{sendBlockedReason}</p>}
        {sendStatus.kind === 'queued' && (
          <p className="text-xs text-text-muted mt-2" role="status">
            Send queued{sendStatus.runId ? ` (run ${sendStatus.runId})` : ''}. Waiting for the mailbox to confirm…
          </p>
        )}
        {sendStatus.kind === 'done' && (
          <p className="text-xs text-success mt-2" role="status">
            Sent {formatDate(est.proposal_sent_at)}.{' '}
            {est.m365_message_id ? 'Mailbox message id recorded; replies will be tracked.' : 'No mailbox message id was recorded, so replies cannot be tracked for this send.'}
          </p>
        )}
        {sendStatus.kind === 'timeout' && (
          <p className="text-xs text-warning mt-2" role="alert">
            No confirmation within 2 minutes{sendStatus.runId ? ` (run ${sendStatus.runId})` : ''}. The send may still be in progress or may have failed on the job runner; this page shows a sent date once the mailbox confirms.
          </p>
        )}
        {sendStatus.kind === 'error' && (
          <p className="text-xs text-error mt-2" role="alert">{sendStatus.message}</p>
        )}
      </section>

      {/* Chain-of-custody: accept estimate → create deployment */}
      {canAccept && (
        <section className="rounded-lg border border-success bg-success-soft p-4" aria-labelledby="accept-heading">
          <h2 id="accept-heading" className="text-sm font-semibold text-text">Customer accepted?</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Accepting marks this estimate as accepted and creates an installing deployment on the customer 360. A Microsoft 365 send is not required.
          </p>
          {(!est.customer_id || !est.location_id) && (
            <p className="text-xs text-warning mt-2">
              This estimate has no customer or location, so accepting cannot create a deployment.
            </p>
          )}
          {acceptError && (
            <p className="text-xs text-error mt-2" role="alert">{acceptError}</p>
          )}
          {acceptedDeploymentId && customer?.id && (
            <p className="text-xs text-success mt-2" role="status">
              Deployment created.{' '}
              <a href={adminCustomerUrl(customer.id)} className="underline">Open customer in admin</a>
            </p>
          )}
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={handleAcceptEstimate}
              disabled={accepting || !est.customer_id || !est.location_id}
              className="inline-flex min-h-11 items-center px-4 py-2 text-sm font-medium rounded-lg bg-success text-text-inverse hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2"
            >
              {accepting ? 'Accepting…' : 'Mark accepted and create deployment'}
            </button>
          </div>
        </section>
      )}

      {est.status === 'accepted' && (
        <p className="rounded-lg border border-success bg-success-soft p-3 text-xs text-text">
          Estimate is accepted. Schedule the field trip from the{' '}
          {customer?.id ? (
            <a href={adminCustomerUrl(customer.id)} className="underline">admin customer 360</a>
          ) : (
            'admin customer 360'
          )}
          .
        </p>
      )}
    </div>
  );
}

function InfoCard({ label, value, hint, bold }: { label: string; value: string; hint?: string; bold?: boolean }) {
  return (
    <div className="bg-surface rounded-lg border border-border p-3 sm:p-4">
      <p className="text-[10px] font-semibold text-text-faint uppercase tracking-wider">{label}</p>
      <p className={`mt-0.5 text-text ${bold ? 'text-lg font-bold' : 'text-sm font-medium'}`}>{value}</p>
      {hint && <p className="text-[11px] text-text-faint">{hint}</p>}
    </div>
  );
}

function Detail({ label, value, emptyText }: { label: string; value: string | null; emptyText?: string }) {
  return (
    <div className="flex justify-between gap-3 sm:block">
      <dt className="text-text-muted text-xs">{label}</dt>
      <dd className={`text-right sm:text-left ${value ? 'text-text' : 'text-text-faint'}`}>{value ?? emptyText ?? '—'}</dd>
    </div>
  );
}

interface LineColumn {
  label: string;
  render: (item: EstimateLineItemRow) => string;
  strong?: boolean;
}

function LineItemSection({
  title,
  items,
  columns,
  emptyText,
}: {
  title: string;
  items: EstimateLineItemRow[];
  columns: LineColumn[];
  emptyText: string;
}) {
  const headingId = `${title.replace(/\s+/g, '-').toLowerCase()}-heading`;
  return (
    <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">{emptyText}</p>
      ) : (
        <>
          {/* Phone: one card per line */}
          <ul className="md:hidden space-y-2" aria-label={title}>
            {items.map((i) => (
              <li key={i.id} className="rounded-md border border-border bg-surface-2 p-3">
                <p className="text-sm font-medium text-text">{i.description}</p>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {columns.map((c) => (
                    <div key={c.label} className="contents">
                      <dt className="text-text-muted">{c.label}</dt>
                      <dd className={`text-right ${c.strong ? 'font-medium text-text' : 'text-text'}`}>{c.render(i)}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-text-muted">
                  <th scope="col" className="pb-2">Description</th>
                  {columns.map((c) => (
                    <th key={c.label} scope="col" className="pb-2 text-right whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {items.map((i) => (
                  <tr key={i.id}>
                    <td className="py-2 text-text">{i.description}</td>
                    {columns.map((c) => (
                      <td key={c.label} className={`py-2 text-right whitespace-nowrap ${c.strong ? 'font-medium text-text' : 'text-text-muted'}`}>{c.render(i)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
